import {
    Activity,
    CommentableObject,
    PrivateDiscussion,
    Role,
    useCommentedItemsQuery,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { useEnrollHandler } from '@hooks/useEnrollmentsWithMapping';
import { CoursePermissions } from '@components/types';
import { useEffect } from 'react';

export function useCommentedItemsData() {
    const [currentUser] = useUserSession();
    const commentedItems = useCommentedItemsQuery(getClient('comments'));
    const { enrolls, loading, isRefetching } = useEnrollHandler();

    useEffect(() => {
        commentedItems.refetch();
    }, [currentUser?.id]);

    const items = commentedItems.data?.commentedItems
        ?.map((item) => {
            switch (item.type) {
                case CommentableObject.PrivateDiscussion:
                    const privateDiscussion = item as PrivateDiscussion;
                    const activity = privateDiscussion.targetItem as Activity;
                    if (!activity) return;
                    const studentEnrollments = privateDiscussion.enrollments.filter(
                        (e) => e.role.type == Role.Student,
                    );
                    const lastCommenterEnrollment = privateDiscussion.enrollments.find(
                        (e) => e.user.id == item.comments.slice(-1)[0].author.id,
                    );
                    if (
                        enrolls.leadingCourses.find(
                            (e) =>
                                e.course.id == activity.unit.course.id &&
                                e.role.permissions.includes(CoursePermissions.reply_to_comments),
                        ) &&
                        studentEnrollments.length == 1
                    )
                        return {
                            url: `/courses/${activity.unit.course.id}/file-viewer/${studentEnrollments[0].id}/${activity.id}`,
                            studentEnrollments,
                            lastCommenterEnrollment,
                            item,
                        };
                    else
                        return {
                            url: `/courses/${activity.unit.course.id}/units/${activity.unit.id}/activities/${activity.id}?tabIndex=2`,
                            studentEnrollments,
                            lastCommenterEnrollment,
                            item,
                        };
            }
        })
        ?.filter((a) => !!a);

    const sent = items?.filter((item) =>
        item.item.comments.find((c) => c.author.id === currentUser.id),
    );

    const allReceived = items; // we decided to keep all items in received, even those that are sent
    const watching = allReceived?.filter((item) =>
        item.item.type == CommentableObject.PrivateDiscussion &&
        (item.item as PrivateDiscussion).enrollments.find((e) => e.user.id == currentUser.id)?.role
            ?.type !== Role.Student
            ? ((item.item as PrivateDiscussion).enrollments.find(
                  (e) =>
                      e.user.id != currentUser.id &&
                      e.user.id ==
                          item.item.comments
                              .filter((c) => c.author.id != currentUser.id)
                              .slice(-1)[0]?.author.id,
              )?.role?.type || Role.Student) !== Role.Student
            : false,
    );
    const received = allReceived?.filter((item) => !watching.includes(item));

    return { all: items, watching, received, sent, isLoading: commentedItems.isLoading };
}
